import { api } from '@goaltree/backend/convex/_generated/api'
import { formatAmount } from '@goaltree/backend/convex/lib/habitMeasure'
import {
  PHOTO_FILTERS,
  type PhotoEdit,
  type PhotoFilterId,
  type Rect,
  STREAK_FLAME,
  type StickerKind,
  type StickerSpec,
  buildStickerSpecs,
  coverCrop,
  isPhotoFilterId,
  toggleSticker,
  updateSticker,
} from '@goaltree/ui/lib/photo-effects'
import { cn } from '@goaltree/ui/lib/utils'
import { useQuery } from 'convex/react'
import { CalendarClock, Check, Flame, Hash, Tag, Trash2 } from 'lucide-react'
import { type PointerEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { habitColor } from '@/components/inbox-habits'
import { useTheme } from '@/components/theme-provider'
import { useCategoryFill } from '@/lib/category-color'
import { logicalDayStr } from '@/lib/day'
import { drawFiltered, drawStickers, loadImage } from '@/lib/photo-bake'

const FILTER_KEY = 'goaltree.photoFilter'

function lastFilter(): PhotoFilterId {
  try {
    const v = localStorage.getItem(FILTER_KEY)
    return isPhotoFilterId(v) ? v : 'original'
  } catch {
    return 'original'
  }
}

/** Edit state for one photo: the device's last filter, no stickers; filter picks are remembered. */
export function usePhotoEdit() {
  const [edit, setEdit] = useState<PhotoEdit>(() => ({ filter: lastFilter(), stickers: [] }))
  const set = (next: PhotoEdit) => {
    if (next.filter !== edit.filter)
      try {
        localStorage.setItem(FILTER_KEY, next.filter)
      } catch {}
    setEdit(next)
  }
  const reset = () => setEdit((e) => ({ filter: e.filter, stickers: [] }))
  return [edit, set, reset] as const
}

function resolveColor(color: string) {
  const v = /^var\((--[\w-]+)\)$/.exec(color)
  if (!v || typeof document === 'undefined') return color
  return getComputedStyle(document.body).getPropertyValue(v[1]).trim() || '#a8ff3e'
}

type HabitRow = NonNullable<ReturnType<typeof useQuery<typeof api.nodes.getHabits>>>[number]

function habitSpecs(
  h: HabitRow,
  date: string,
  lang: string,
  fill: (c: string) => string,
): StickerSpec[] {
  const today = date === logicalDayStr(Date.now())
  const amount = h.measure?.fast
    ? null
    : today
      ? (h.measureToday?.total ?? 0)
      : (h.dayAmounts.find((d) => d.date === date)?.amount ?? 0)
  return buildStickerSpecs({
    title: h.title,
    color: resolveColor(fill(habitColor(h.category))),
    streak: h.streakDays ?? 0,
    amount: h.measure && amount ? { value: amount, unit: h.measure.unit } : null,
    glyph: h.dot?.kind === 'emoji' ? h.dot.emoji : h.dot?.kind === 'smiley' ? '😊' : null,
    when: { ms: today ? Date.now() : new Date(`${date}T12:00:00`).getTime(), today },
    locale: lang,
    formatAmount,
  })
}

/** Stickers from one habit's data for the photo's day. */
export function useHabitStickers(habitId: string, date: string): StickerSpec[] {
  const { i18n } = useTranslation()
  const fill = useCategoryFill()
  const habits = useQuery(api.nodes.getHabits, {})
  const h = habits?.find((x) => x._id === habitId)
  return useMemo(
    () => (h ? habitSpecs(h, date, i18n.language, fill) : []),
    [h, date, i18n.language, fill],
  )
}

/** Story stickers: the habit ticked off most recently today, else just the date. */
export function useStoryStickers(): StickerSpec[] {
  const { i18n } = useTranslation()
  const fill = useCategoryFill()
  const habits = useQuery(api.nodes.getHabits, {})
  return useMemo(() => {
    const today = logicalDayStr(Date.now())
    let best: { h: HabitRow; at: number } | null = null
    for (const h of habits ?? []) {
      const at = Math.max(0, ...h.completions.filter((ms) => logicalDayStr(ms) === today))
      if (at && (!best || at > best.at)) best = { h, at }
    }
    if (best) return habitSpecs(best.h, today, i18n.language, fill)
    return buildStickerSpecs({
      title: '',
      color: resolveColor('var(--primary)'),
      streak: 0,
      when: { ms: Date.now(), today: true },
      locale: i18n.language,
      formatAmount,
    }).filter((s) => s.kind === 'time')
  }, [habits, i18n.language, fill])
}

function useCssSize(ref: React.RefObject<HTMLElement | null>) {
  const [w, setW] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width))
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref])
  return w
}

const dpr = () => Math.min(3, typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1)

// The image placed so its `aspect` crop fills a w×h canvas.
function placement(img: HTMLImageElement, w: number, h: number, aspect: number): Rect {
  const crop = coverCrop(img.naturalWidth, img.naturalHeight, aspect)
  const s = w / crop.w
  return { x: -crop.x * s, y: -crop.y * s, w: img.naturalWidth * s, h: img.naturalHeight * s }
}

type Drag = { kind: StickerKind; x0: number; y0: number; sx: number; sy: number; moved: boolean }

/** The live preview: filtered photo in an `aspect` frame, stickers drawn on top and draggable. */
export function PhotoEditorCanvas({
  source,
  aspect,
  specs,
  edit,
  onEdit,
  selected,
  onSelect,
  className,
}: {
  source: Blob
  aspect: number
  specs: readonly StickerSpec[]
  edit: PhotoEdit
  onEdit: (e: PhotoEdit) => void
  selected: StickerKind | null
  onSelect: (k: StickerKind | null) => void
  className?: string
}) {
  const { t } = useTranslation()
  const wrap = useRef<HTMLDivElement>(null)
  const base = useRef<HTMLCanvasElement>(null)
  const top = useRef<HTMLCanvasElement>(null)
  const boxes = useRef<{ kind: StickerKind; box: Rect }[]>([])
  const drag = useRef<Drag | null>(null)
  const [img, setImg] = useState<HTMLImageElement | null>(null)
  const cssW = useCssSize(wrap)
  const cssH = cssW / aspect
  const k = dpr()

  useEffect(() => {
    let live = true
    void loadImage(source)
      .then((el) => live && setImg(el))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [source])

  useEffect(() => {
    const c = base.current
    if (!c || !img || !cssW) return
    c.width = Math.round(cssW * k)
    c.height = Math.round(cssH * k)
    const ctx = c.getContext('2d', { willReadFrequently: true })
    if (ctx) drawFiltered(ctx, img, placement(img, c.width, c.height, aspect), edit.filter)
  }, [img, cssW, cssH, k, aspect, edit.filter])

  useEffect(() => {
    const c = top.current
    if (!c || !cssW) return
    c.width = Math.round(cssW * k)
    c.height = Math.round(cssH * k)
    const ctx = c.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, c.width, c.height)
    boxes.current = drawStickers(ctx, edit, specs, { x: 0, y: 0, w: c.width, h: c.height })
    const sel = boxes.current.find((b) => b.kind === selected)
    if (sel) {
      const p = 4 * k
      ctx.save()
      ctx.setLineDash([5 * k, 4 * k])
      ctx.lineWidth = 1.5 * k
      ctx.strokeStyle = '#ffffff'
      ctx.shadowColor = 'rgba(0,0,0,0.5)'
      ctx.shadowBlur = 3 * k
      ctx.strokeRect(sel.box.x - p, sel.box.y - p, sel.box.w + p * 2, sel.box.h + p * 2)
      ctx.restore()
    }
  }, [edit, specs, selected, cssW, cssH, k])

  const at = (e: PointerEvent) => {
    const r = e.currentTarget.getBoundingClientRect()
    return { x: (e.clientX - r.left) * k, y: (e.clientY - r.top) * k }
  }
  const hit = (x: number, y: number) =>
    [...boxes.current]
      .reverse()
      .find(
        ({ box: b }) => x >= b.x - 8 && x <= b.x + b.w + 8 && y >= b.y - 8 && y <= b.y + b.h + 8,
      )

  return (
    <div
      ref={wrap}
      data-vaul-no-drag
      className={cn('relative w-full shrink-0 overflow-hidden bg-black', className)}
      style={{ aspectRatio: aspect }}
    >
      {!img && <div className="absolute inset-0 animate-pulse bg-muted" />}
      <canvas ref={base} className="absolute inset-0 size-full" aria-hidden />
      <canvas
        ref={top}
        role="img"
        aria-label={t('photoEdit.previewLabel')}
        className={cn('absolute inset-0 size-full', edit.stickers.length > 0 && 'touch-none')}
        onPointerDown={(e) => {
          const p = at(e)
          const h = hit(p.x, p.y)
          if (!h) {
            onSelect(null)
            return
          }
          const s = edit.stickers.find((x) => x.kind === h.kind)
          if (!s) return
          e.currentTarget.setPointerCapture(e.pointerId)
          drag.current = { kind: h.kind, x0: p.x, y0: p.y, sx: s.x, sy: s.y, moved: false }
        }}
        onPointerMove={(e) => {
          const d = drag.current
          if (!d) return
          const p = at(e)
          const dx = p.x - d.x0
          const dy = p.y - d.y0
          if (!d.moved && Math.hypot(dx, dy) < 5 * k) return
          d.moved = true
          const c = e.currentTarget
          onEdit(updateSticker(edit, d.kind, { x: d.sx + dx / c.width, y: d.sy + dy / c.height }))
        }}
        onPointerUp={() => {
          const d = drag.current
          drag.current = null
          if (d && !d.moved) onSelect(selected === d.kind ? null : d.kind)
          else if (d) onSelect(d.kind)
        }}
        onPointerCancel={() => {
          drag.current = null
        }}
      />
    </div>
  )
}

function FilterThumb({ img, filter }: { img: HTMLImageElement | null; filter: PhotoFilterId }) {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const c = ref.current
    if (!c || !img) return
    const s = Math.round(56 * dpr())
    c.width = c.height = s
    const ctx = c.getContext('2d', { willReadFrequently: true })
    if (ctx) drawFiltered(ctx, img, placement(img, s, s, 1), filter)
  }, [img, filter])
  return <canvas ref={ref} className="size-14 rounded-xl bg-muted" aria-hidden />
}

function StickerChip({ spec }: { spec: StickerSpec }) {
  const { t } = useTranslation()
  const { resolvedTheme } = useTheme()
  if (spec.kind === 'streak')
    return (
      <>
        <Flame
          className="size-3.5"
          strokeWidth={2.25}
          style={{ color: resolvedTheme === 'light' ? STREAK_FLAME.light : STREAK_FLAME.dark }}
        />
        {spec.text}
      </>
    )
  if (spec.kind === 'dot')
    return (
      <>
        <span
          className="grid size-4 place-items-center rounded-full text-[9px] leading-none"
          style={{ backgroundColor: spec.color }}
        >
          {spec.glyph ?? <Check className="size-2.5 text-[#0b0c0b]" strokeWidth={3.5} />}
        </span>
        {t('photoEdit.stickers.dot')}
      </>
    )
  const Icon = spec.kind === 'time' ? CalendarClock : spec.kind === 'amount' ? Hash : Tag
  return (
    <>
      <Icon
        className="size-3.5 shrink-0"
        style={spec.kind === 'habit' ? { color: spec.color } : undefined}
      />
      <span className="max-w-[9rem] truncate">{spec.text}</span>
    </>
  )
}

const pill =
  'flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-bold transition-colors disabled:cursor-default disabled:opacity-60'

/** Filter strip, sticker tray and the selected sticker's controls, under a PhotoEditorCanvas. */
export function PhotoEditorControls({
  source,
  specs,
  edit,
  onEdit,
  selected,
  onSelect,
  disabled,
}: {
  source: Blob
  specs: readonly StickerSpec[]
  edit: PhotoEdit
  onEdit: (e: PhotoEdit) => void
  selected: StickerKind | null
  onSelect: (k: StickerKind | null) => void
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const [img, setImg] = useState<HTMLImageElement | null>(null)
  useEffect(() => {
    let live = true
    void loadImage(source)
      .then((el) => live && setImg(el))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [source])
  const sel = edit.stickers.find((s) => s.kind === selected)
  return (
    <div className="flex min-w-0 flex-col gap-2.5">
      <div
        role="radiogroup"
        aria-label={t('photoEdit.filtersLabel')}
        className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none]"
      >
        {PHOTO_FILTERS.map((f) => {
          const on = edit.filter === f.id
          return (
            <button
              key={f.id}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={disabled}
              onClick={() => onEdit({ ...edit, filter: f.id })}
              className="flex shrink-0 cursor-pointer flex-col items-center gap-1 disabled:cursor-default"
            >
              <span
                className={cn(
                  'rounded-[14px] p-0.5 ring-2 transition-colors',
                  on ? 'ring-foreground' : 'ring-transparent',
                )}
              >
                <FilterThumb img={img} filter={f.id} />
              </span>
              <span
                className={cn(
                  'text-[11px] font-bold',
                  on ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {t(`photoEdit.filters.${f.id}`)}
              </span>
            </button>
          )
        })}
      </div>
      {specs.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-bold text-foreground/80">
            {t('photoEdit.stickersLabel')}
          </span>
          <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-0.5 [scrollbar-width:none]">
            {specs.map((s) => {
              const on = edit.stickers.some((x) => x.kind === s.kind)
              return (
                <button
                  key={s.kind}
                  type="button"
                  aria-pressed={on}
                  aria-label={t(`photoEdit.stickers.${s.kind}`)}
                  disabled={disabled}
                  onClick={() => {
                    onEdit(toggleSticker(edit, s.kind))
                    onSelect(on ? null : s.kind)
                  }}
                  className={cn(
                    pill,
                    on
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-border text-foreground hover:bg-muted',
                  )}
                >
                  <StickerChip spec={s} />
                </button>
              )
            })}
          </div>
        </div>
      )}
      {sel ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <div className="inline-flex rounded-full border border-border p-0.5">
            {(['light', 'dark'] as const).map((tone) => (
              <button
                key={tone}
                type="button"
                aria-pressed={sel.tone === tone}
                onClick={() => onEdit(updateSticker(edit, sel.kind, { tone }))}
                className={cn(
                  'h-7 cursor-pointer rounded-full px-3 text-[12px] font-bold',
                  sel.tone === tone ? 'bg-foreground text-background' : 'text-muted-foreground',
                )}
              >
                {t(`photoEdit.${tone}`)}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-full border border-border p-0.5">
            {([1, 1.5] as const).map((size) => (
              <button
                key={size}
                type="button"
                aria-pressed={sel.size === size}
                onClick={() => onEdit(updateSticker(edit, sel.kind, { size }))}
                className={cn(
                  'h-7 cursor-pointer rounded-full px-3 text-[12px] font-bold',
                  sel.size === size ? 'bg-foreground text-background' : 'text-muted-foreground',
                )}
              >
                {t(size === 1 ? 'photoEdit.small' : 'photoEdit.large')}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              onEdit(toggleSticker(edit, sel.kind))
              onSelect(null)
            }}
            className="flex h-8 cursor-pointer items-center gap-1 rounded-full px-2.5 text-[12px] font-bold text-destructive hover:bg-muted"
          >
            <Trash2 className="size-3.5" />
            {t('photoEdit.remove')}
          </button>
        </div>
      ) : edit.stickers.length > 0 ? (
        <p className="text-[11.5px] text-muted-foreground">{t('photoEdit.dragHint')}</p>
      ) : null}
    </div>
  )
}
